import { Lead, LeadCreate, LeadUpdate, LeadListResponse } from './types';

// Base API URL - configurable for production
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

/**
 * API service for communicating with the FastAPI backend
 */
class ApiService {
  
  /**
   * Fetch all leads with optional pagination and filtering
   */
  async getLeads(page: number = 1, perPage: number = 50, status?: string): Promise<LeadListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (status) {
      params.append('status', status);
    }
    
    const response = await fetch(`${API_BASE}/leads?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch leads: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  /**
   * Create a new lead
   */
  async createLead(leadData: LeadCreate): Promise<Lead> {
    const response = await fetch(`${API_BASE}/leads/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(leadData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to create lead: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  /**
   * Update an existing lead
   */
  async updateLead(leadId: string, updates: LeadUpdate): Promise<Lead> {
    const response = await fetch(`${API_BASE}/leads/${leadId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to update lead: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  /**
   * Check API health
   */
  async healthCheck(): Promise<{ status: string }> {
    const response = await fetch(`${API_BASE}/health`);
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }
    
    return response.json();
  }
}

// Export singleton instance
export const apiService = new ApiService();